<template>
  <v-container
    fluid
    class="squad-status"
    v-if="currentTeam && squad && ordered">
    <!-- <v-layout column align-center justify-center> -->
    <v-text-field
      dark
      solo-inverted
      prepend-inner-icon="fas fa-search"
      v-model="search"
      label="Search"
      clearable
      class="search-line"></v-text-field>
    <div class="players-list">
      <v-layout
        align-start
        class="player-row"
        v-for="player in filteredSquad"
        :key="player.uid"
        @click="showPlayerStatus(player.uid)">
        <PlayerAvatarInfo
          :player="player"
          class="squad-status-player-avatar"></PlayerAvatarInfo>
        <ReportContent
          v-if="$store.getters['reports/lastReports'](player.uid, 2).length > 0"
          class="squad-status-report"
          :reports="$store.getters['reports/lastReports'](player.uid, 2)"
          :user="player"
          :userId="player.uid"
          :view-diff="showReportsDiff"></ReportContent>
        <div v-else class="squad-status-no-report">No Reports available</div>
      </v-layout>
    </div>

    <footer class="buttons-bar">
      <v-btn
        class="pdf-button"
        :loading="this.pdfReady === 'loading'"
        :color="this.pdfBtnTextColor"
        @click.prevent="handlePdfClick()">
        {{ pdfText }}
        <v-icon>picture_as_pdf</v-icon>
      </v-btn>
      <v-tooltip top color="white">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            icon
            elevation="2"
            class="arrows-button"
            @click="showReportsDifference()">
            <v-icon class="mdi mdi-swap-vertical"></v-icon>
          </v-btn>
        </template>
        <span style="color: black">Show Reports Difference</span>
      </v-tooltip>
    </footer>
  </v-container>
</template>

<script>
import { mapActions } from "vuex"
import PlayerAvatarInfo from "@/components/shared/common/PlayerAvatarInfo.vue"
import ReportContent from "@/components/routes/playerStatus/strips/ReportContent.vue"
import constants from "@/constants.js"
import colors from "@/assets/sass/_variables.sass"
import { sendToMakePdf } from "@/api/pdfUtil.ts"
import { orderBy } from "lodash-es"
// import { getImgStorageBlob } from "@/api/firebase.api.js"

export default {
  name: "SquadStatus",
  components: { PlayerAvatarInfo, ReportContent },
  data() {
    return {
      timeOut: null,
      pdf: null,
      pdfReady: "false",
      resp: null,
      search: "",
      months: constants.months,
      ordered: false,
      orderedSquad: null,
      viewStat: 0,
      viewType: 0,
      viewTime: 3,
      updateChart: false,
      icons: constants.icons,
      constants: constants.playerStatus,
      showReportsDiff: false,
    }
  },

  async mounted() {
    this.$store.commit("setOverlay", true)
    if (!this.currentTeam) {
      await this.awaitActiveTeamOnReload()
    }
    await this.loadPlayerReports()
    this.$store.commit("setOverlay", false)
  },
  beforeDestroy() {
    clearTimeout(this.timeOut)
  },
  computed: {
    pdfBtnTextColor() {
      if (this.pdfReady === "true") {
        return colors["bright-green"]
      }
      if (this.pdfReady === "loading") {
        return colors["bright-blue"]
      }
      if (this.pdfReady === "error") {
        return colors.peach
      }
      return colors.white
    },
    pdfText() {
      if (this.pdfReady === "true") {
        return "Download"
      }
      if (this.pdfReady === "loading") {
        return "Loading..."
      }
      if (this.pdfReady === "error") {
        return "There was an issue. Try again."
      }
      return "Generate"
    },
    filteredSquad() {
      const tmpSquad = this.orderedSquad
      if (!this.search || this.search.trim() === "") {
        return tmpSquad
      }
      const filterRegExp = new RegExp(this.search, "gi")
      return (
        tmpSquad &&
        tmpSquad.filter((player) => {
          return (
            filterRegExp.test(player.firstName) ||
            filterRegExp.test(player.lastName)
          )
        })
      )
    },

    currentTeam() {
      return this.$store.getters["teamList/activeTeam"]
    },

    squad() {
      return this.$store.getters["teamList/activeTeamPlayers"]
    },

    initialized() {
      if (this.squad.length === 0) {
        return false
      }
      return (
        Object.keys(this.$store.state.reports.playerReports).length >=
        this.squad.length
      )
    },
  },

  methods: {
    async getImageAsBase64(url) {
      try {
        const headers = new Headers()
        const response = await fetch(url, {
          method: "GET",
          headers,
        })
        const blob = await response.blob()
        const base64String = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.readAsDataURL(blob)
          reader.onloadend = () => {
            const base64String = reader.result?.toString().split(",")[1]
            if (base64String) {
              resolve(base64String)
            } else {
              reject(new Error("Failed to convert image to base64"))
            }
          }
          reader.onerror = () => {
            reject(new Error("Failed to read image file"))
          }
        })
        return base64String
      } catch (e) {
        console.error("failed to convert url to base64 img", e)
        return null
      }
    },
    async handlePdfClick() {
      if (this.pdfReady === "error") {
        this.pdf = null
        this.pdfReady = "false"
        return
      }
      if (this.pdfReady === "true") {
        const url = URL.createObjectURL(this.pdf)
        const link = document.createElement("a")
        link.href = url
        link.setAttribute(
          "download",
          `${this.currentTeam.teamName} Squad Status.pdf`
        )
        link.click()
        this.pdf = null
        this.pdfReady = "false"
      }
      if (this.pdfReady === "false") {
        this.pdfReady = "loading"
        try {
          const data = []
          for (const player of this.squad) {
            const fullName = `${player?.firstName ?? "n/a"} ${
              player?.lastName ?? "n/a"
            }`

            const report =
              this.$store.state.reports.playerReports[player.uid]?.[0] ?? {}
            const date = Intl.DateTimeFormat("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            }).format(report?.date ?? Date.now())
            const soreness = report?.soreness ?? "n/a"
            const stress = report?.stress ?? "n/a"
            const sleep = report?.sleep ?? "n/a"
            const fatigue = report?.fatigue ?? "n/a"
            // const energy = report?.energy ?? "n/a"
            let readiness =
              ((11 - soreness + (11 - fatigue) + (11 - stress) + (11 - sleep)) *
                10) /
              4
            if (Number.isNaN(readiness)) {
              readiness = "n/a"
            }
            data.push([
              fullName,
              date,
              soreness,
              stress,
              // energy,
              fatigue,
              sleep,
              readiness,
            ])
          }
          const b64Img = await this.getImageAsBase64(this.currentTeam.imgUrl)
          const pdfPayload = {
            header: `${this.currentTeam.teamName} Squad Status`,
            data,
          }
          if (b64Img) {
            pdfPayload.logo = `data:image/jpeg;base64,${b64Img}`
          }
          this.resp = await sendToMakePdf(pdfPayload)
          this.pdf = this.resp.data
          if (this.pdf) {
            this.pdfReady = "true"
          } else {
            throw new Error("pdf is null")
          }
        } catch (e) {
          console.error(e)
          this.pdfReady = "error"
        }
      }
    },
    awaitActiveTeamOnReload() {
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          const teamData = this.currentTeam || {}
          if (Object.keys(teamData).length) {
            clearInterval(interval)
            resolve(teamData)
          }
        }, 1000)
      })
    },
    ...mapActions({
      loadReports: "reports/loadAllUsersReports",
    }),
    // async init() {
    //   if (!this.initialized) {
    //     this.$store.commit("setOverlay", true)
    //   }

    //   this.loadPlayerReports()
    // },

    setSquadReports() {
      this.squad.forEach((player) => {
        const lastReport = this.$store.getters["reports/lastReports"](
          player.uid,
          1
        )
        player.reportDate =
          lastReport.length > 0
            ? lastReport[0].timeStamp
            : this.$moment(new Date(1970, 0, 1))
      })
      if (!this.orderedSquad) {
        this.orderedSquad = this.squad
      }
      this.orderedSquad = orderBy(this.squad, (player) => player.reportDate, [
        "desc",
      ])
      this.ordered = true
    },

    async loadPlayerReports() {
      if (!this.squad.length) {
        await this.awaitActiveTeamOnReload()
      }
      // this.squad.forEach((player) => this.loadReports({ userId: player.uid }))
      await this.loadReports(this.squad.map((player) => player.uid))
      this.setSquadReports()
    },

    showPlayerStatus(idPlayer) {
      this.$router.push({ name: "PlayerStatus", params: { id: idPlayer } })
    },

    showReportsDifference() {
      this.showReportsDiff = !this.showReportsDiff
    },
  },
}
</script>

<style scoped lang="sass">
@import '@/assets/sass/_variables.sass'
.container
  margin: 0px


.search-line, .v-input, .v-input__slot
  z-index: 10
  flex: 0 1 auto
  opacity: 1
  border-radius: 10px !important

  .v-label, .v-icon
    color: #555555

.squad-status
  padding: 10px
  min-height: 60px
  color: white
  position: relative
  width: 100%
  height: 100%
  display: flex
  flex-direction: column
  .players-list
    width: 100%
    // height: 73vh
    overflow-y: scroll

    .player-row
      width: 99%
      margin: auto
      border-bottom: 1px solid #b0b0b0

      &:hover
        cursor: pointer

      .squad-status-report
        padding: 0px 0px 10px 10px !important
        // margin-top: 10px

      .squad-status-player-avatar
        padding-top: 10px
        max-width: 90px

      .squad-status-no-reports
        margin: auto
        text-align: center
        flex: 2

.buttons-bar
  display: flex
  justify-content: space-between
  text-align: right
  padding: 10px 20px
  position: absolute
  width: 100%
  bottom: 0
  .arrows-button
    background-color: white
    color: black !important


  .pdf-button > span > i
    margin-left: 0.3rem
.squad-status-no-report
  min-height: 185px
  flex: 2 1 100%
  align-items: center
  justify-content: center
  display: flex
  width: 100%
.no-print
  display: block
</style>
